import { RowState } from './dataset.js';
import { setHtml } from './css.js';
import { esc } from './grid-util.js';
export class GridUndo {
    // ── 개인정보 마스킹 해제 (사유 + 감사) ──
    isUnmasked(row, col) { return this.revealed.has(row + ':' + col); }
    /** @internal */ remask(k) { const t = this.revealed.get(k); if (t)
        clearTimeout(t); if (this.revealed.delete(k))
        this.bump(); }
    /** 감사 이벤트 만들기 (1.2.0: role·context 포함). context 는 auditContext 옵션 — 함수면 지금 부른 값 */
    /** @internal */ auditEvent(e) {
        const ac = this.opts.auditContext;
        let context;
        try {
            context = typeof ac === 'function' ? ac() : ac;
        }
        catch (err) {
            console.warn(err);
            context = undefined;
        }
        return { ...e, at: new Date().toISOString(), grid: this, role: this.getRole(), context };
    }
    /** 해제돼 있으면 다시 가리고, 아니면 사유를 받아 감사 기록 후 해제 */
    async toggleUnmask(row, col) {
        const k = row + ':' + col;
        if (this.revealed.has(k)) {
            this.remask(k);
            return false;
        }
        const c = this.cols.find(x => x.id === col);
        const um = c?.def.unmask;
        if (!c || !c.def.privacy || !um)
            return false;
        const cfg = typeof um === 'object' ? um : {};
        let ok;
        if (cfg.onRequest)
            ok = await cfg.onRequest(row, col);
        else {
            const reason = await this.askReason(c.def.header ?? col);
            ok = reason != null;
            if (ok) {
                try {
                    ok = (await this.opts.onAudit?.(this.auditEvent({ action: 'unmask', row, key: this.ds.keyOf(row), col, reason: reason }))) !== false;
                }
                catch (e) {
                    console.warn(this.t('감사 기록 실패 — 해제하지 않음'), e);
                    ok = false;
                }
            }
        }
        this.focusGrid();
        if (!ok)
            return false;
        const vi = this.disp.indexOf(row), ci = this.cols.indexOf(c);
        if (vi >= 0 && (this.focus.vi !== vi || this.focus.ci !== ci))
            this.setFocus(vi, ci, false);
        this.revealed.set(k, setTimeout(() => this.remask(k), (cfg.seconds ?? 30) * 1000));
        this.bump();
        return true;
    }
    /** 열람 사유 입력 창 (필수). 취소하면 null */
    /** @internal */ askReason(label) {
        return new Promise(resolve => {
            const back = document.createElement('div');
            back.className = 'og-dlgb';
            setHtml(back, `<div class="og-dlg" role="dialog" aria-modal="true" aria-labelledby="og-dlg-t"><div class="og-dlgh" id="og-dlg-t">개인정보 열람 — ${label.replace(/[<>&]/g, '')}</div>
<label class="og-dlgl">${this.t('열람 사유 (필수, 감사 기록에 남습니다)')}<select class="og-rsel"><option value="">${this.t('직접 입력')}</option><option>진료 목적 본인 확인</option><option>검사 결과 동일인 확인</option><option>보험 청구 확인</option></select><textarea class="og-rtxt" rows="2" maxlength="200" aria-label="${this.t('열람 사유')}"></textarea></label>
<div class="og-dlgerr" role="alert"></div><div class="og-dlgf"><button type="button" class="og-rok">${this.t('해제')}</button><button type="button" class="og-rno">${this.t('취소')}</button></div></div>`);
            this.root.appendChild(back);
            const txt = back.querySelector('.og-rtxt'), sel = back.querySelector('.og-rsel'), err = back.querySelector('.og-dlgerr');
            sel.onchange = () => { if (sel.value)
                txt.value = sel.value; };
            const done = (v) => { back.remove(); resolve(v); };
            const ok = () => { const v = txt.value.trim(); if (!v) {
                err.textContent = this.t('사유를 입력하세요');
                txt.focus();
                return;
            } done(v); };
            back.querySelector('.og-rok').onclick = ok;
            back.querySelector('.og-rno').onclick = () => done(null);
            back.addEventListener('keydown', e => { e.stopPropagation(); if (e.key === 'Escape')
                done(null);
            else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ok();
            } });
            txt.focus();
        });
    }
    /** 셀 오류 표시와 오류 목록 지우기 */
    // ── 셀 메모 ──
    /** @internal */ notesOn() { return !!(this.opts.notes || this.opts.noteStore); }
    /** 메모 저장소에서 다시 읽음 */
    async reloadNotes() {
        const st = this.opts.noteStore;
        if (!st)
            return;
        try {
            this.ds.loadNotes(await st.load());
        }
        catch { /* 못 읽으면 메모 없이 */ }
    }
    getNote(row, col) { return this.ds.getNote(row, col); }
    /** 메모 쓰기·지우기(text 빈 값) + 저장소 반영. 실패하면 되돌리고 false */
    async setNote(row, col, text) {
        const prev = this.ds.getNote(row, col);
        const n = this.ds.setNote(row, col, text, this.opts.noteUser);
        if (!n)
            return true;
        const st = this.opts.noteStore;
        if (!st || n.rowKey.startsWith('#'))
            return true; // 저장 전 행(키 없음)은 화면에만
        try {
            if (n.note)
                await st.save(n);
            else
                await st.remove(n.rowKey, n.col);
            return true;
        }
        catch (e) {
            this.ds.setNote(row, col, prev?.note ?? null, prev?.userId);
            this.showTip(this.t('메모를 저장하지 못했습니다: ') + (e?.message ?? e));
            setTimeout(() => this.hideTip(), 2500);
            return false;
        }
    }
    /** 메모 편집 창 */
    editNote(row, col) {
        return new Promise(resolve => {
            const cur = this.ds.getNote(row, col);
            const c = this.cols.find(x => x.id === col);
            const back = document.createElement('div');
            back.className = 'og-dlgb og-notedlg';
            const dlg = document.createElement('div');
            dlg.className = 'og-dlg';
            dlg.setAttribute('role', 'dialog');
            dlg.setAttribute('aria-modal', 'true');
            const h = document.createElement('div');
            h.className = 'og-dlgh';
            h.textContent = `${this.t('메모')} — ${c?.def.header ?? col}`;
            const body = document.createElement('div');
            body.className = 'og-dlgl';
            const ta = document.createElement('textarea');
            ta.rows = 4;
            ta.maxLength = 2000;
            ta.value = cur?.note ?? '';
            ta.setAttribute('aria-label', this.t('메모 내용'));
            body.appendChild(ta);
            if (cur?.userId) {
                const m = document.createElement('small');
                m.textContent = `${cur.userId} ${cur.at?.replace('T', ' ').slice(0, 16) ?? ''}`;
                body.appendChild(m);
            }
            const f = document.createElement('div');
            f.className = 'og-dlgf';
            const done = async (v) => { back.remove(); this.focusGrid(); resolve(v === undefined ? false : await this.setNote(row, col, v)); };
            const btn = (label, fn, cls = '') => { const b = document.createElement('button'); b.type = 'button'; b.textContent = label; if (cls)
                b.className = cls; b.onclick = fn; f.appendChild(b); };
            if (cur)
                btn(this.t('삭제'), () => done(null));
            btn(this.t('취소'), () => done(undefined));
            btn(this.t('저장'), () => done(ta.value), 'og-rok');
            dlg.append(h, body, f);
            back.appendChild(dlg);
            this.root.appendChild(back);
            back.addEventListener('keydown', e => { e.stopPropagation(); if (e.key === 'Escape')
                done(undefined);
            else if (e.key === 'Enter' && e.ctrlKey)
                done(ta.value); });
            ta.focus();
        });
    }
    undo() {
        if (this.rowEdit && this.rowEdit.vals.size) {
            this.cancelRowEdit();
            return;
        } // 행 편집 중이면 버퍼만 버림
        const e = this.undoStack.pop();
        if (!e)
            return;
        this.cancelEdit();
        this.redoStack.push(this.applyUndo(e));
        if (this.undoHistEl)
            this.renderUndoHistory();
    }
    /** 다시 실행 (Ctrl+Y / Ctrl+Shift+Z) */
    redo() {
        const e = this.redoStack.pop();
        if (!e)
            return;
        this.cancelEdit();
        this.undoStack.push(this.applyUndo(e));
        if (this.undoHistEl)
            this.renderUndoHistory();
    }
    /** 실행 취소 기록 (오래된 것부터). label·at·셀 수·행 수 */
    getUndoHistory() {
        return this.undoStack.map(e => ({ label: e.label, at: e.at, cells: e.cells.length, rows: e.rows.length }));
    }
    canUndo() { return this.undoStack.length > 0; }
    canRedo() { return this.redoStack.length > 0; }
    /** 최근 n 건을 한 번에 실행 취소 */
    undoTo(n) { for (let i = 0; i < n && this.undoStack.length; i++)
        this.undo(); }
    /**
     * fn 안의 변경(셀 값·grid.addRow/deleteRows·ds.addRow 로 붙은 행)을 실행 취소 한 번으로 묶는다.
     * 끌어 놓기·가져오기·붙여넣기 처리에. 동기 함수만 (await 뒤 변경은 따로 기록됨)
     */
    undoBatch(label, fn) {
        if (this.undoGroup)
            return fn();
        const g = { label, at: Date.now(), cells: [], rows: [], len0: this.ds.length };
        this.undoGroup = g;
        try {
            return fn();
        }
        finally {
            this.undoGroup = null;
            const known = new Set(g.rows.map(x => x.row));
            for (let r = g.len0; r < this.ds.length; r++)
                if (!known.has(r) && this.ds.rowState(r) === RowState.INSERT)
                    g.rows.push({ row: r, before: RowState.DISCARDED, after: RowState.INSERT });
            // 새 행의 셀 값은 행 상태로 되돌리므로 셀 기록에서 뺀다
            const fresh = new Set(g.rows.filter(x => x.before === RowState.DISCARDED).map(x => x.row));
            const cells = g.cells.filter(c => !fresh.has(c.row));
            this.pushUndo({ label: g.label, at: g.at, cells, rows: g.rows, ...(g.order ? { order: g.order } : {}) });
        }
    }
    /** @internal */ pushUndo(e) {
        if (!e.cells.length && !e.rows.length && !e.order)
            return;
        const g = this.undoGroup;
        if (g) {
            g.cells.push(...e.cells);
            g.rows.push(...e.rows);
            if (e.order && !g.order)
                g.order = e.order;
            return;
        }
        this.undoStack.push(e);
        this.redoStack = [];
        if (this.undoStack.length > 200)
            this.undoStack.shift();
        if (this.undoHistEl)
            this.renderUndoHistory();
    }
    /** 기록 한 건을 되돌리고, 다시 적용할 반대 기록을 돌려준다 */
    /** @internal */ applyUndo(e) {
        const inv = { label: e.label, at: e.at, cells: [], rows: e.rows.map(x => ({ row: x.row, before: x.after, after: x.before })) };
        const rowsChanged = e.rows.length > 0;
        this.ds.batch(() => {
            // 셀은 행 상태보다 먼저 (삭제 취소 → 값 되돌림 순서와 무관하게 같은 결과)
            for (let i = e.cells.length - 1; i >= 0; i--) {
                const x = e.cells[i];
                inv.cells.unshift({ row: x.row, col: x.col, old: this.ds.get(x.row, x.col) });
                this.ds.set(x.row, x.col, x.old);
            }
            for (const x of e.rows)
                this.ds.restoreRowState(x.row, x.before);
        });
        for (const x of e.rows)
            if (x.before === RowState.DISCARDED || x.before === RowState.DELETE) {
                this.checked.delete(x.row);
                this.clearAsync(r => r === x.row);
            }
        if (rowsChanged)
            this.rebuild();
        const fr = this.getFocusedRow();
        if (e.order) {
            inv.order = Array.from(this.view);
            this.restoreViewOrder(e.order);
        }
        for (const x of e.cells)
            this.scheduleAsync(x.row, x.col);
        const first = e.order ? fr : e.cells[0]?.row ?? e.rows.find(x => x.before !== RowState.DISCARDED && x.before !== RowState.DELETE)?.row;
        if (first != null) {
            const vi = this.disp.indexOf(first);
            if (vi >= 0)
                this.setFocus(vi, Math.max(0, e.cells[0] && !e.order ? this.cols.findIndex(c => c.id === e.cells[0].col) : this.focus.ci), false);
        }
        this.bump();
        return inv;
    }
    /** 실행 취소 기록 창 (우클릭 "실행 취소 기록…"). 항목을 누르면 그 기록까지 되돌린다 */
    openUndoHistory() {
        this.closeUndoHistory();
        const p = document.createElement('div');
        p.className = 'og-undoh';
        p.setAttribute('role', 'dialog');
        p.setAttribute('aria-label', this.t('실행 취소 기록'));
        this.undoHistEl = p;
        this.root.appendChild(p);
        p.addEventListener('click', e => {
            const b = e.target.closest('[data-n],[data-a]');
            if (!b)
                return;
            if (b.dataset.a === 'x') {
                this.closeUndoHistory();
                this.focusGrid();
                return;
            }
            if (b.dataset.a === 'redo') {
                this.redo();
                return;
            }
            this.undoTo(+b.dataset.n);
        });
        p.addEventListener('keydown', e => { if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeUndoHistory();
            this.focusGrid();
        } });
        this.renderUndoHistory();
        p.querySelector('button')?.focus();
    }
    closeUndoHistory() { this.undoHistEl?.remove(); this.undoHistEl = null; }
    /** @internal */ renderUndoHistory() {
        const p = this.undoHistEl;
        if (!p)
            return;
        const t = (n) => { const d = new Date(n); return [d.getHours(), d.getMinutes(), d.getSeconds()].map(x => String(x).padStart(2, '0')).join(':'); };
        const list = [...this.undoStack].reverse();
        setHtml(p, `<div class="og-undohh"><b>${this.t('실행 취소 기록')}</b><button type="button" data-a="x" aria-label="${this.t('닫기')}">×</button></div>`
            + (list.length ? `<ol>${list.map((e, i) => `<li><button type="button" data-n="${i + 1}" title="${this.t('여기까지 실행 취소')}">${esc(e.label)}<span>${e.rows.length ? e.rows.length + '행' : e.cells.length + '셀'} · ${t(e.at)}</span></button></li>`).join('')}</ol>` : '<div class="og-undoe">' + this.t('기록 없음') + '</div>')
            + `<div class="og-undof"><button type="button" data-a="redo"${this.redoStack.length ? '' : ' disabled'}>다시 실행 (${this.redoStack.length})</button></div>`);
    }
}
